import { useState, type ReactNode } from 'react';
import { useIntl } from '@/react-intl';
import { Button } from '@/design-system/ds-0/components/Button';
const WARNING_GLYPH = '⚠';

export interface WarningGroup { code: string; count: number; blocking?: boolean; label: ReactNode }
export function warningGroups<T extends { code: string; count?: number }>(issues: readonly T[], label: (issue: T) => ReactNode): WarningGroup[] {
  const groups = new Map<string, WarningGroup>();
  for (const issue of issues) {
    const previous = groups.get(issue.code);
    groups.set(issue.code, { code: issue.code, count: (previous?.count ?? 0) + (issue.count ?? 1), label: previous?.label ?? label(issue) });
  }
  return [...groups.values()];
}
export const importantWarnings = (groups: readonly WarningGroup[]) => [...groups]
  .sort((a, b) => Number(Boolean(b.blocking)) - Number(Boolean(a.blocking)) || b.count - a.count || a.code.localeCompare(b.code))
  .slice(0, 3);

/** Le compteur vient du rapport ; la liste complète conserve son rendu et ses contrôles. */
export default function WarningsSummary({ count, groups = [], children, label }: {
  count: number; groups?: readonly WarningGroup[]; children?: ReactNode; label?: ReactNode;
}) {
  const intl = useIntl();
  const [full, setFull] = useState(false);
  if (count <= 3) return <div data-warnings-summary>{label}<div>{children}</div></div>;
  return <div data-warnings-summary className="rounded-md border border-warning-solid bg-warning-subtle p-3 text-sm text-warning-text">
    <details>
      <summary className="ui-focus-ring cursor-pointer tabular-nums pointer-coarse:min-h-11">
        <span aria-hidden="true">{WARNING_GLYPH} </span>{label ?? intl.formatMessage({ id: 'moduleStudio.warningsSummary.count' }, { n: count })}
      </summary>
      <ul className="list-disc ps-5">{importantWarnings(groups).map(group => <li key={group.code}>{group.label} <bdi className="tabular-nums">{group.count}</bdi></li>)}</ul>
    </details>
    {children ? <Button variant="secondary" size="sm" aria-expanded={full} onClick={() => setFull(!full)}>{intl.formatMessage({ id: full ? 'moduleStudio.warningsSummary.hide' : 'moduleStudio.warningsSummary.all' }, { n: count })}</Button> : null}
    {full ? <div className="mt-3 text-portal-ink">{children}</div> : null}
  </div>;
}
